import { useEffect, useState } from 'react';


import { useApplication } from '@eneco/common/application';
import useDC from '@eneco/dc/useDC';
import { useActor } from '@xstate/react';

import { addressMachine } from '../machines/addressMachine';
import type { FlowContext } from '../types/machine';
import { useFlowHooks } from '../utils/FlowProvider';

export const useFlowQueryParams = () => {
  const { useFlowActorRef } = useFlowHooks();
  const sendToFlowMachine = useFlowActorRef().send;

  const [addressState, sendToAddressMachine] = useActor(addressMachine);

  const { searchParams } = useApplication();
  const { businessUnit, label } = useDC();

  const [initialContextValues, setInitialContextValues] = useState<FlowContext>({});
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const tempInitialContextValues = { ...initialContextValues };

    for (const [key, value] of searchParams.entries()) {
      const contextKey = key as keyof FlowContext;
      if (value.toLowerCase() === 'true' || value.toLowerCase() === 'false') {
        (tempInitialContextValues[contextKey] as unknown) = value === 'true';
      } else {
        (tempInitialContextValues[contextKey] as unknown) = value;
      }
    }

    const { postalCode, houseNumber, houseNumberSuffix } = tempInitialContextValues;

    if (postalCode && houseNumber) {
      tempInitialContextValues.houseNumber = Number(houseNumber);

      if (houseNumberSuffix) {
        tempInitialContextValues.houseNumberSuffix = houseNumberSuffix.toUpperCase();
      }

      setInitialContextValues(tempInitialContextValues);

      sendToAddressMachine({
        type: 'GET_ADDRESS',
        config: {
          businessUnit,
          label,
        },
        values: {
          postalCode,
          houseNumber: Number(houseNumber),
        },
      });
    } else {
      setInitialContextValues(tempInitialContextValues);
      setIsInitialized(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (addressState.matches('SUCCESS') || addressState.matches('ERROR') || addressState.matches('NOT_FOUND')) {
      const tempInitialContextValues = { ...initialContextValues };

      if (addressState.matches('SUCCESS')) {
        const { street, city, houseNumberSuffixes } = addressState.context;

        if (tempInitialContextValues.houseNumberSuffix) {
          if (!houseNumberSuffixes?.includes(tempInitialContextValues.houseNumberSuffix)) {
            delete tempInitialContextValues.houseNumberSuffix;
          }
        }

        tempInitialContextValues.street = street || undefined;
        tempInitialContextValues.city = city || undefined;
      } else {
        delete tempInitialContextValues.postalCode;
        delete tempInitialContextValues.houseNumber;
        delete tempInitialContextValues.houseNumberSuffix;
      }

      setInitialContextValues(tempInitialContextValues);
      setIsInitialized(true);
    }
  }, [addressState]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isInitialized) {
      sendToFlowMachine({
        type: 'SET_INITIAL_CONTEXT',
        values: {
          ...initialContextValues,
        },
      });
    }
  }, [isInitialized, initialContextValues]); // eslint-disable-line react-hooks/exhaustive-deps
};
